import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "src/context";
import { ProForm, ProCard } from "@ant-design/pro-components";
import {
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Space,
  Input,
  Form,
  Select,
  Upload,
  message,
} from "antd";
import { SyncOutlined, UploadOutlined } from "@ant-design/icons";
import { SelectType } from "src/modules/setting/user-management/types";
import coreFunctions from "src/common/utils/coreFunctions";
import apiRequest from "src/common/utils/apiRequest";
import { useNavigate, useParams } from "react-router-dom";

import { DBUser } from "src/modules/setting/user-management/types";

const { Title } = Typography;

const AddUser: React.FC = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [departments, setDepartments] = useState<SelectType[]>([]);
  const [userRoles, setUserRoles] = useState<SelectType[]>([]);
  const CORE_FUNCTIONS = new coreFunctions();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form] = ProForm.useForm();

  const navigate = useNavigate();

  const handleRegeneratePassword = () => {
    const newPassword = Math.random().toString(36).slice(-8);
    form.setFieldsValue({ password: newPassword });
  };

  const { id } = useParams();

  const { dispatch } = useContext(AppContext);

  const handleSave = () => {
    form
      .validateFields()
      .catch((err) => {
        message.error(err.errorFields[0].errors[0]);
      })
      .then(async (values) => {
        setSaving(true);

        const ENDPOINT = id ? "user/edit" : "user/add";
        const API_REQUEST = new apiRequest(ENDPOINT, true);

        let departments = [];
        let role = {};

        if (values.departments.length > 0) {
          departments = values.departments.map((item: any) => {
            if (item.value) {
              return item.value;
            } else {
              return item;
            }
          });
        }

        if (values.role.value) {
          role = values.role.value;
        } else {
          role = values.role;
        }

        await API_REQUEST.send(
          { ...values, id: id, departments, role },
          (data: { status: string; message: string; user: string }) => {
            if (data.status === "success") {
              let profile_image = form.getFieldValue("profile_image");
              if (profile_image && profile_image.fileList.length > 0) {
                dispatch({
                  type: "TRIGGER_UPLOAD_DATA",
                  data: {
                    files: profile_image.fileList,
                    type: "users",
                    value: data.user,
                  },
                });
              }
              message.success(data.message);
              navigate("/modules/setting/users");
            } else {
              message.error(data.message);
            }
          }
        );

        setSaving(false);
      });
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      const API_REQUEST = new apiRequest("user/get", true);

      API_REQUEST.send(
        { id },
        (data: { status: string; message: string; data: DBUser }) => {
          if (data.status === "success") {
            let preloadData: any = { ...data.data };
            preloadData.departments = data.data.departments.map((item: any) => {
              return { label: item.name, value: item.id };
            });
            preloadData.role = {
              label: data.data.role.name,
              value: data.data.role.id,
            };

            form.setFieldsValue(preloadData);

            setLoading(false);
          } else {
            message.error(data.message);
            navigate("/modules/setting/users");
          }
        }
      );
    }
  }, [id]);

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
      }}
    >
      <Title level={3} style={{ marginBottom: "20px", color: "#595959" }}>
        {id ? "Edit" : "Add New"} User
      </Title>
      <Row justify="start">
        <Col xs={24} sm={24} md={24} lg={24}>
          <ProCard
            loading={loading}
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            }}
          >
            <ProForm
              disabled={saving}
              layout="vertical"
              onFinish={handleSave}
              form={form}
              submitter={false}
            >
              {/* Profile Image */}
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Form.Item
                    name="profile_image"
                    label={
                      <span style={{ fontSize: "14px" }}>Profile Image</span>
                    }
                  >
                    <Upload
                      maxCount={1}
                      listType="picture"
                      beforeUpload={() => false}
                    >
                      <Button icon={<UploadOutlined />}>
                        Upload Profile Image
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              {/* First Name and Last Name */}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="first_name"
                    label={<span style={{ fontSize: "14px" }}>First Name</span>}
                    rules={[
                      { required: true, message: "First Name is required!" },
                    ]}
                  >
                    <Input placeholder="Enter First Name" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="last_name"
                    label={<span style={{ fontSize: "14px" }}>Last Name</span>}
                    rules={[
                      { required: true, message: "Last Name is required!" },
                    ]}
                  >
                    <Input placeholder="Enter Last Name" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Email */}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="email"
                    label={<span style={{ fontSize: "14px" }}>Email</span>}
                    rules={[
                      { required: true, message: "Email is required!" },
                      { type: "email", message: "Enter a valid email!" },
                    ]}
                  >
                    <Input placeholder="Enter Email" size="large" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="departments"
                    label={<span style={{ fontSize: "14px" }}>Department</span>}
                    rules={[
                      { required: true, message: "Department is required!" },
                    ]}
                  >
                    <Select
                      placeholder="Department"
                      mode="multiple"
                      size="large"
                      showSearch
                      filterOption={false}
                      onInputKeyDown={(e) =>
                        CORE_FUNCTIONS.searchOptions(
                          e.currentTarget.value,
                          "global",
                          "departments",
                          setDepartments
                        )
                      }
                      onClick={(e) =>
                        CORE_FUNCTIONS.searchOptions(
                          "",
                          "global",
                          "departments",
                          setDepartments
                        )
                      }
                      options={departments.map((department) => ({
                        label: department.name,
                        value: department.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Department */}
              <Row gutter={[16, 16]}></Row>

              {/* Password */}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="role"
                    label={<span style={{ fontSize: "14px" }}>Role</span>}
                    rules={[{ required: true, message: "Role is required!" }]}
                  >
                    <Select
                      placeholder="Role"
                      size="large"
                      showSearch
                      filterOption={false}
                      onInputKeyDown={(e) =>
                        CORE_FUNCTIONS.searchOptions(
                          e.currentTarget.value,
                          "global",
                          "user_roles",
                          setUserRoles
                        )
                      }
                      onClick={(e) =>
                        CORE_FUNCTIONS.searchOptions(
                          "",
                          "global",
                          "user_roles",
                          setUserRoles
                        )
                      }
                      options={userRoles.map((row) => ({
                        label: row.name,
                        value: row.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={12}>
                  <Form.Item
                    name="password"
                    label={<span style={{ fontSize: "14px" }}>Password</span>}
                    rules={[
                      {
                        required: id ? false : true,
                        message: "Password is required!",
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder="Enter Password"
                      size="large"
                      visibilityToggle={{
                        visible: passwordVisible,
                        onVisibleChange: setPasswordVisible,
                      }}
                      addonAfter={
                        <Space>
                          <Button
                            size="small"
                            onClick={handleRegeneratePassword}
                          >
                            <SyncOutlined />
                          </Button>
                        </Space>
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: "20px 0", borderColor: "#e6e6e6" }} />

              {/* Buttons */}
              <Row justify="end" gutter={[16, 16]}>
                <Space>
                  <Button onClick={() => console.log("Cancel clicked")}>
                    Cancel
                  </Button>
                  <Button color="primary" variant="solid" htmlType="submit">
                    Save User
                  </Button>
                </Space>
              </Row>
            </ProForm>
          </ProCard>
        </Col>
      </Row>
    </div>
  );
};

export default AddUser;
